/**
 * The signing page's markup, one function of its stage. Kept apart from the
 * page's hooks so each state renders in a test without a browser.
 *
 * Number matching is the point of the code box: the CLI, the extension or the
 * desktop app shows the same code, and the person checks the two agree before
 * signing. The code always comes from the server's challenge, and is shown
 * only when the page was opened from a link (`showCode`): when the portal
 * itself sent the person here there is no other screen to match.
 */
import { Box, Flex, Heading, Stack, Text } from "@chakra-ui/react";
import { forwardRef, ReactNode } from "react";
import type { IconType } from "react-icons";
import {
  TbAlertTriangle,
  TbCheck,
  TbClock,
  TbFingerprint,
  TbLinkOff,
  TbSignature,
} from "react-icons/tb";

import Card from "components/ds/Card";
import PloyButton from "components/ds/PloyButton";
import PasskeyEnrolment from "components/passkeys/PasskeyEnrolment";
import type { StepUpChallenge } from "state/members/stepUp";
import Linker from "ui/Linker";

import { clockLabel, msUntil } from "./clock";
import { signatureKind, type KnownAccount } from "./payloadProse";
import {
  EXPIRED_LINE,
  PayloadSummary,
  SigningBadge,
  USED_LINE,
  type Tone,
} from "./signingParts";

export type StepUpStage =
  | { name: "loading" }
  | { name: "not_found"; message: string }
  | { name: "load_failed"; message: string }
  /**
   * `notice: "not_allowed"`: the browser closed the prompt without signing.
   * It will not say whether the person cancelled or this device has no
   * passkey for the account, so the page offers both ways on.
   * `adding`: adding a passkey on this device before signing, for a member
   * whose passkeys are on other devices (asked for after that notice, or by
   * the portal's "Add one here"). `offerAdd`: left that with Cancel, so a
   * quiet link next to Sign goes back to it without a prompt failing first.
   */
  | {
      name: "review";
      enrolled?: boolean;
      notice?: "not_allowed";
      adding?: boolean;
      offerAdd?: boolean;
    }
  | { name: "signing" }
  | { name: "signed"; kind: "submit" | "approve" | "unknown" }
  | { name: "expired" }
  | { name: "used"; message?: string }
  | { name: "failed"; message: string };

export const NOT_ALLOWED_NOTICE =
  "The passkey prompt closed without signing. If this device has no passkey for your account, add one here, or open this page on a device that has one.";

export type StepUpViewProps = {
  stage: StepUpStage;
  challenge?: StepUpChallenge;
  /** The account the payload names, as the request it came from names it. */
  account?: KnownAccount | null;
  now: number;
  /** False when this browser's clock disagrees with the server's. */
  clockTrusted: boolean;
  /** Whether this browser can run a passkey prompt at all. */
  webAuthn: boolean;
  /**
   * Whether to show the code to match: only for a page opened from a link.
   * The portal's own visits have no other screen showing one.
   */
  showCode: boolean;
  returnTo: string;
  returnLabel: string;
  onSign: () => void;
  onEnrolled: () => void;
  /** Start (true) or leave (false) adding a passkey on this device. */
  onAddHere: (adding: boolean) => void;
  onRetry: () => void;
};

const Lines = ({ lines }: { lines: string[] }) => (
  <Stack spacing="sm">
    {lines.map((line) => (
      <Text key={line} fontSize="smd" color="neutral.700">
        {line}
      </Text>
    ))}
  </Stack>
);

const ReturnLink = ({
  to,
  label,
  variant = "secondary",
}: {
  to: string;
  label: string;
  variant?: "primary" | "secondary";
}) => (
  <Linker to={to} alignSelf="flex-start">
    <PloyButton variant={variant} size="md">
      {label}
    </PloyButton>
  </Linker>
);

const CodeBox = ({ code }: { code: string }) => (
  <Box
    bg="neutral.50"
    borderWidth="1px"
    borderColor="neutral.200"
    borderRadius="component.xl"
    p="lg"
  >
    <Text fontSize="xs" fontWeight={500} color="neutral.700">
      Code
    </Text>
    <Text
      data-testid="step-up-code"
      fontFamily="mono"
      fontSize="3xl"
      fontWeight={600}
      letterSpacing="0.2em"
      color="neutral.950"
      lineHeight="1.2"
      mt="2xs"
    >
      {code}
    </Text>
    <Flex gap="1xs" align="flex-start" mt="sm">
      <Box
        as={TbAlertTriangle}
        boxSize="16px"
        mt="2px"
        color="warning.700"
        flexShrink={0}
        aria-hidden
      />
      <Text fontSize="smd" color="neutral.900">
        If the command line, the browser extension or the desktop app sent you
        here, check it shows this same code.{" "}
        <Text as="strong" fontWeight={600}>
          If the codes differ, do not sign.
        </Text>
      </Text>
    </Flex>
  </Box>
);

/** A member's first passkey, asked for above the code: nothing signs without it. */
const FirstPasskey = ({ onEnrolled }: { onEnrolled: () => void }) => (
  <Stack spacing="md">
    <Box>
      <Text fontSize="md" fontWeight={600} color="neutral.900">
        Step 1: add a passkey
      </Text>
      <Text fontSize="smd" color="neutral.700" mt="2xs">
        You need a passkey to sign. It lives on this device or in your password
        manager, and you unlock it the way you unlock your device. Then you sign
        this request with it.
      </Text>
    </Box>
    <PasskeyEnrolment onCreated={onEnrolled} />
  </Stack>
);

/** Says the passkey was made, so the Sign button that follows is not read as it failing. */
const PasskeyAdded = () => (
  <Flex
    role="status"
    gap="sm"
    align="flex-start"
    bg="success.50"
    borderWidth="1px"
    borderColor="success.200"
    borderRadius="component.xl"
    p="md"
  >
    <Box
      as={TbCheck}
      boxSize="20px"
      color="success.600"
      flexShrink={0}
      aria-hidden
    />
    <Text fontSize="smd" color="neutral.900">
      <Text as="strong" fontWeight={600}>
        Passkey added.
      </Text>{" "}
      Now sign this request with it.
    </Text>
  </Flex>
);

const Review = ({
  challenge,
  account,
  stage,
  now,
  clockTrusted,
  webAuthn,
  showCode,
  onSign,
  onEnrolled,
  onAddHere,
}: {
  challenge: StepUpChallenge;
  account?: KnownAccount | null;
  stage: StepUpStage;
  now: number;
  clockTrusted: boolean;
  webAuthn: boolean;
  showCode: boolean;
  onSign: () => void;
  onEnrolled: () => void;
  onAddHere: (adding: boolean) => void;
}) => {
  const left = msUntil(challenge.expiresAt, now);
  const runningOut = clockTrusted && left < 60_000;
  const signing = stage.name === "signing";
  const enrolled = stage.name === "review" && stage.enrolled;
  const notAllowed = stage.name === "review" && stage.notice === "not_allowed";
  const addingHere = stage.name === "review" && Boolean(stage.adding);
  // The declined-prompt button already goes back to adding: one way, not two.
  const offerAdd =
    stage.name === "review" && Boolean(stage.offerAdd) && !enrolled && !notAllowed;

  return (
    <Stack spacing="lg" w="full">
      <PayloadSummary challenge={challenge} account={account} />

      {!challenge.hasPasskeys && <FirstPasskey onEnrolled={onEnrolled} />}

      {showCode && <CodeBox code={challenge.code} />}

      <Text
        fontSize="smd"
        color={runningOut ? "warning.700" : "neutral.700"}
        fontWeight={runningOut ? 600 : 400}
        sx={{ fontVariantNumeric: "tabular-nums" }}
      >
        {clockTrusted
          ? `Expires in ${clockLabel(left)}`
          : "Expires five minutes after it was asked for"}
      </Text>

      {challenge.hasPasskeys && !webAuthn && (
        <Text role="alert" fontSize="smd" color="neutral.800">
          This browser cannot use passkeys. Open this page in a recent version
          of Chrome, Safari, Edge or Firefox.
        </Text>
      )}

      {challenge.hasPasskeys && webAuthn && (
        <Stack spacing="sm">
          {enrolled && <PasskeyAdded />}
          {notAllowed && !addingHere && (
            <Text role="status" fontSize="smd" color="neutral.900">
              {NOT_ALLOWED_NOTICE}
            </Text>
          )}
          {addingHere ? (
            <Stack spacing="md">
              <Text fontSize="md" fontWeight={600} color="neutral.900">
                Add a passkey on this device
              </Text>
              <PasskeyEnrolment
                onCreated={onEnrolled}
                onCancel={() => onAddHere(false)}
              />
            </Stack>
          ) : (
            <Flex gap="sm" wrap="wrap" direction={{ base: "column", sm: "row" }}>
              <PloyButton
                variant="primary"
                size="md"
                onClick={onSign}
                isLoading={signing}
                loadingText="Waiting for your passkey"
                // Not the fingerprint "Create passkey" carries: the two are
                // different steps, and should not look like a retry.
                leftIcon={<TbSignature size={16} aria-hidden />}
              >
                Sign with passkey
              </PloyButton>
              {notAllowed && (
                <PloyButton
                  variant="secondary"
                  size="md"
                  onClick={() => onAddHere(true)}
                >
                  Add a passkey on this device
                </PloyButton>
              )}
              {offerAdd && (
                <PloyButton
                  variant="azurite-link"
                  fontSize="smd"
                  alignSelf={{ base: "flex-start", sm: "center" }}
                  onClick={() => onAddHere(true)}
                >
                  Add a passkey on this device
                </PloyButton>
              )}
            </Flex>
          )}
        </Stack>
      )}
    </Stack>
  );
};

const reviewTitle = (challenge?: StepUpChallenge) => {
  const kind = challenge
    ? signatureKind(challenge.payload, challenge.purpose)
    : "unknown";
  if (kind === "submit") return "Sign your access request";
  if (kind === "approve") return "Approve with your passkey";
  return "Sign with your passkey";
};

const StepUpView = forwardRef<HTMLHeadingElement, StepUpViewProps>(
  (
    {
      stage,
      challenge,
      account,
      now,
      clockTrusted,
      webAuthn,
      showCode,
      returnTo,
      returnLabel,
      onSign,
      onEnrolled,
      onAddHere,
      onRetry,
    },
    headingRef,
  ) => {
    const view = ((): {
      title: string;
      tone: Tone;
      icon: IconType;
      body: ReactNode;
    } => {
      switch (stage.name) {
        case "not_found":
          return {
            title: "This signing link is not valid",
            tone: "warning",
            icon: TbLinkOff,
            body: (
              <Stack spacing="lg">
                <Lines
                  lines={[
                    stage.message,
                    "Links to sign only work for the person they were made for.",
                  ]}
                />
                <ReturnLink to={returnTo} label={returnLabel} />
              </Stack>
            ),
          };
        case "load_failed":
          return {
            title: "We could not load this",
            tone: "error",
            icon: TbAlertTriangle,
            body: (
              <Stack spacing="lg">
                <Lines lines={[stage.message]} />
                <PloyButton
                  variant="secondary"
                  size="md"
                  onClick={onRetry}
                  alignSelf="flex-start"
                >
                  Try again
                </PloyButton>
              </Stack>
            ),
          };
        case "signed":
          return {
            title:
              stage.kind === "approve" ? "Approval signed" : "Request signed",
            tone: "success",
            icon: TbCheck,
            body: (
              <Stack spacing="lg">
                <Lines
                  lines={[
                    stage.kind === "approve"
                      ? "Your approval is recorded."
                      : "Your request is signed and submitted.",
                    "If you started this somewhere else, you can close this tab and go back to it.",
                  ]}
                />
                <ReturnLink to={returnTo} label={returnLabel} variant="primary" />
              </Stack>
            ),
          };
        case "expired":
          return {
            title: "This signing request has expired",
            tone: "warning",
            icon: TbClock,
            body: (
              <Stack spacing="lg">
                <Lines
                  lines={[
                    EXPIRED_LINE,
                    "Start again from where you asked to get a new one.",
                  ]}
                />
                <ReturnLink to={returnTo} label={returnLabel} />
              </Stack>
            ),
          };
        case "used":
          return {
            title: "This signing request was already used",
            tone: "warning",
            icon: TbAlertTriangle,
            body: (
              <Stack spacing="lg">
                <Lines
                  lines={[
                    ...(stage.message ? [stage.message] : []),
                    USED_LINE,
                  ]}
                />
                <ReturnLink to={returnTo} label={returnLabel} />
              </Stack>
            ),
          };
        case "failed":
          return {
            title: "Signing did not work",
            tone: "error",
            icon: TbAlertTriangle,
            body: (
              <Stack spacing="lg">
                <Lines lines={[stage.message, "Nothing was signed."]} />
                <Flex gap="sm" wrap="wrap">
                  <PloyButton variant="primary" size="md" onClick={onRetry}>
                    Try again
                  </PloyButton>
                  <ReturnLink to={returnTo} label={returnLabel} />
                </Flex>
              </Stack>
            ),
          };
        case "loading":
          return {
            title: "Loading",
            tone: "p",
            icon: TbFingerprint,
            body: (
              <Text fontSize="smd" color="neutral.700">
                Getting what you are about to sign.
              </Text>
            ),
          };
        default:
          return {
            title: reviewTitle(challenge),
            tone: "p",
            icon: TbFingerprint,
            body: challenge ? (
              <Review
                challenge={challenge}
                account={account}
                stage={stage}
                now={now}
                clockTrusted={clockTrusted}
                webAuthn={webAuthn}
                showCode={showCode}
                onSign={onSign}
                onEnrolled={onEnrolled}
                onAddHere={onAddHere}
              />
            ) : null,
          };
      }
    })();

    return (
      <Box maxW="560px" mx="auto" w="full" py="lg">
        <Card>
          <Stack spacing="lg">
            <Stack spacing="md" align="center" textAlign="center">
              <SigningBadge
                tone={view.tone}
                icon={view.icon}
                waiting={stage.name === "signing"}
              />
              <Heading
                as="h1"
                ref={headingRef}
                tabIndex={-1}
                fontSize="xl"
                fontWeight={600}
                color="neutral.950"
                lineHeight="1.25"
                _focus={{ outline: "none" }}
              >
                {view.title}
              </Heading>
            </Stack>
            {view.body}
          </Stack>
        </Card>
      </Box>
    );
  },
);

StepUpView.displayName = "StepUpView";

export default StepUpView;
